// ============================================================
// Loading plan import — parse a pasted table or CSV/TSV file,
// map its columns to our fields and validate each row.
// Excel copy-paste arrives as tab-separated text.
// ============================================================

import { carriers } from '@/data/arrivalsData';

export const FIELDS = ['orderId', 'customer', 'material', 'weight', 'carrier', 'date', 'time', 'type'];
export const REQUIRED = ['orderId', 'carrier', 'date', 'time'];

// Header names seen in SAP / Excel exports (cs, en, de, pl), lower-case.
const SYNONYMS = {
  orderId: ['dodávka', 'dodavka', 'delivery', 'lieferung', 'dostawa', 'vbeln', 'číslo dodávky'],
  customer: ['zákazník', 'zakaznik', 'customer', 'kunde', 'odbiorca', 'příjemce'],
  material: ['materiál', 'material', 'materiał', 'produkt'],
  weight: ['množství', 'mnozstvi', 'quantity', 'menge', 'ilość', 't', 'tuny', 'hmotnost'],
  carrier: ['dopravce', 'carrier', 'spediteur', 'przewoźnik', 'spedice'],
  date: ['datum', 'date', 'datum nakládky', 'data'],
  time: ['čas', 'cas', 'time', 'uhrzeit', 'godzina', 'čas nakládky'],
  type: ['typ', 'type', 'balení', 'silo/palety'],
};

export function parseTable(text) {
  const lines = text.replace(/\r/g, '').split('\n').filter(l => l.trim());
  if (!lines.length) return { headers: [], rows: [] };
  const sep = lines[0].includes('\t') ? '\t' : lines[0].includes(';') ? ';' : ',';
  const cells = lines.map(l => l.split(sep).map(c => c.trim().replace(/^"|"$/g, '')));
  return { headers: cells[0], rows: cells.slice(1) };
}

// Best guess of which column holds which field.
export function autoMap(headers) {
  const mapping = {};
  FIELDS.forEach(f => {
    const i = headers.findIndex(h => SYNONYMS[f].includes(h.toLowerCase()));
    if (i >= 0) mapping[f] = i;
  });
  return mapping;
}

const findCarrier = (name) => {
  const n = (name || '').toLowerCase();
  return carriers.find(c => c.id.toLowerCase() === n || c.name.toLowerCase().startsWith(n) || n.startsWith(c.name.toLowerCase().split(' ')[0]));
};

// Accepts 3. 11. 2026, 3.11.2026, 2026-11-03.
function toDate(dateStr, timeStr) {
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(dateStr);
  const cz = /^(\d{1,2})\.\s*(\d{1,2})\.\s*(\d{4})$/.exec(dateStr);
  const [y, m, d] = iso ? [iso[1], iso[2], iso[3]] : cz ? [cz[3], cz[2], cz[1]] : [];
  const time = /^(\d{1,2}):(\d{2})$/.exec(timeStr || '');
  if (!y || !time) return null;
  return new Date(Number(y), Number(m) - 1, Number(d), Number(time[1]), Number(time[2]));
}

// One preview row per input row: values, problems (error = not imported).
export function validate(rows, mapping, existingOrderIds) {
  const seen = new Set();
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return rows.map((cells, index) => {
    const get = (f) => (mapping[f] != null ? cells[mapping[f]] || '' : '');
    const errors = [];
    const warnings = [];
    REQUIRED.forEach(f => { if (!get(f)) errors.push(`missing.${f}`); });
    const carrier = get('carrier') ? findCarrier(get('carrier')) : null;
    if (get('carrier') && !carrier) errors.push('unknownCarrier');
    const plannedAt = toDate(get('date'), get('time'));
    if (get('date') && get('time') && !plannedAt) errors.push('badDate');
    if (plannedAt && plannedAt < today) errors.push('pastDate');
    const orderId = get('orderId');
    if (orderId && (existingOrderIds.has(orderId) || seen.has(orderId))) errors.push('duplicate');
    seen.add(orderId);
    if (!get('material')) warnings.push('missing.material');
    return {
      index,
      orderId,
      customer: get('customer'),
      material: get('material'),
      weight: Number(String(get('weight')).replace(',', '.')) || null,
      type: /palet|pallet|big bag|25 kg/i.test(get('type') + get('material')) ? 'pallet' : 'silo',
      carrier,
      carrierRaw: get('carrier'),
      plannedAt,
      errors,
      warnings,
    };
  });
}
